// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { NavigateFunction } from "react-router-dom";

import { RoutedProps, withRouter } from "view/reactrouterinterceptor";
import { MainPanel } from "types";
import { isSet } from "utils";
import { ActionType, IAction, raiseAction } from "actions";
import { tabClickHandler } from "guiutils";
import { IState } from "states/states";
import { IOcppChargingSession, IRfid } from "states/ocppstates";

import FormattedTime from "./FormattedTime";
import Rfid from "model/rfid";
import SearchFilter from "./SearchFilter";

interface IRfidListExtProps {
  isDashboard: boolean;
  showOnlyCharging?: boolean;
}

interface IRfidListProps {
  selectedRfid: number | undefined;
  rfids: IRfid[];
  pendingSession: IOcppChargingSession[];
}

interface IRfidListState {
  filterText: string;
}

const mapStateToProps = (state: IState) => {
  const props: IRfidListProps = {
    rfids: state.ocpp.rfid,
    selectedRfid: state.router.rfid,
    pendingSession: state.ocpp.pendingChargingSession,
  };
  return props;
};

interface IRfidListDispatch {
  onSelectRfid: (rfid: number | undefined, navigate: NavigateFunction) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IRfidListDispatch = {
    onSelectRfid: (rfidId: number | undefined, navigate: NavigateFunction) => {
      if (isSet(rfidId)) {
        navigate(`../${MainPanel.RFIDDETAIL}?rfid=${rfidId}`);
      } else {
        navigate(`../${MainPanel.RFIDDETAIL}`);
      }
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  placeholderSearchField: {
    id: "Search for cards...",
  },
  tootTipSearchField: {
    id: "Search for cards-numbers, names or categories. To search for multiple fields, separate search texts by a space!",
  },
});

class RfidListComponent extends React.Component<
  IRfidListProps & IRfidListExtProps & IRfidListDispatch & WrappedComponentProps & RoutedProps,
  IRfidListState
> {
  public state: IRfidListState = {
    filterText: "",
  };

  public render() {
    const rfidIndicis = Array.from(Array(this.props.rfids.length).keys())
      .filter((curIndex) => this.props.rfids[curIndex].isActive)
      .sort((a, b) => {
        const keyA = this.props.rfids[a]?.name
          ? this.props.rfids[a]?.name
          : this.props.rfids[a]?.rfid;
        const keyB = this.props.rfids[b]?.name
          ? this.props.rfids[b]?.name
          : this.props.rfids[b]?.rfid;
        return isSet(keyA) && isSet(keyB) && keyA > keyB ? 1 : -1;
      });
    const filterTextsLowerCase = this.state.filterText.toLocaleLowerCase().split(" ");
    const rfidItems = rfidIndicis
      .filter((rfid, rfidIndex) => {
        const pendingSession = this.props.pendingSession.find(
          (aSession) => this.props.rfids[rfid].rfid === aSession.rfid,
        );
        return (
          (this.props.showOnlyCharging !== true || isSet(pendingSession)) &&
          (this.state.filterText === "" ||
            !filterTextsLowerCase.some(
              (filterTextLowerCase) =>
                filterTextLowerCase !== "" &&
                !this.props.rfids[rfid].rfid.toLocaleLowerCase().includes(filterTextLowerCase) &&
                !this.props.rfids[rfid].name?.toLocaleLowerCase().includes(filterTextLowerCase) &&
                !this.props.rfids[rfid].category?.toLocaleLowerCase().includes(filterTextLowerCase),
            ))
        );
      })
      .map((curIndex) => this.renderRfid(curIndex));
    const className = ["link", "all-sessions"];
    if (this.props.selectedRfid === undefined) {
      className.push("selected");
    }

    if (this.props.rfids.length === 0) {
      return <></>;
    }

    return (
      <>
        {this.props.isDashboard === false && (
          <SearchFilter
            className="tableSearchBoxSidebar"
            filterText={this.state.filterText}
            placeholder={this.props.intl.formatMessage(messages.placeholderSearchField)}
            tooltip={this.props.intl.formatMessage(messages.tootTipSearchField)}
            onChange={(filterText) => {
              this.setState({ filterText });
            }}
          />
        )}
        <div id="rfidlist">
          <div id="rfidlistframe">
            <table>
              <tbody>
                <tr
                  key="norfid"
                  className={className.join(" ")}
                  tabIndex={0}
                  onKeyDown={tabClickHandler}
                  onClick={() => this.props.onSelectRfid(undefined, this.props.navigate)}
                >
                  <td className="status-circle"></td>
                  <td colSpan={2}>
                    <FormattedMessage id="show all charging sessions" />
                  </td>
                </tr>
                {rfidItems}
              </tbody>
            </table>
            {rfidItems.length === 0 && (
              <div>
                {this.props.showOnlyCharging ? (
                  <FormattedMessage id="No charging card with active charging session found." />
                ) : (
                  <FormattedMessage id="No charging card found." />
                )}
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  private renderRfid(rfidIndex: number) {
    const rfid = this.props.rfids[rfidIndex];
    const pendingSession = this.props.pendingSession.find((curItem) => curItem.rfid === rfid.rfid);
    const className = ["link"];
    if (rfid.id === this.props.selectedRfid) {
      className.push("selected");
    }

    // render rfid, and potentially connector rows
    return (
      <tr
        key={rfidIndex}
        className={className.join(" ")}
        tabIndex={0}
        onKeyDown={tabClickHandler}
        onClick={() => this.props.onSelectRfid(rfid.id, this.props.navigate)}
      >
        <td className="status-circle">{this.renderStatusIcon(rfid, pendingSession)}</td>
        <td>{rfid.name ? rfid.name : rfid.rfid}</td>
        <td className="rfid-status">
          {pendingSession ? (
            <>
              <FormattedMessage id="Charging since" />:{" "}
              <FormattedTime valueMs={pendingSession.startTimestamp * 1000} />
            </>
          ) : (
            <></>
          )}
        </td>
      </tr>
    );
  }

  private renderStatusIcon(rfid: IRfid, pendingSession: IOcppChargingSession | undefined) {
    const color = new Rfid(rfid, pendingSession).getColor();
    return (
      <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: color }}></div>
    );
  }
}

export default injectIntl(
  connect(mapStateToProps, mapDispatchToProps)(withRouter(RfidListComponent)),
);
